import { FC, ReactNode, useCallback } from 'react';
import { useForm, Controller, FormProvider, FormState } from 'react-hook-form';
import { Grid, TextField, Stack, FormControlLabel } from '@mui/material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { CountrySelect } from './Field/CountrySelect';

export type FormData = Tributes.DeliveryDetails;

interface Props {
  onSubmitRequest: (values: FormData) => void;
  onFieldChange?: (fieldName: keyof FormData, value: any) => void;
  slots?: {
    footer?: ReactNode | ((formState: FormState<FormData>) => ReactNode);
  }
  defaultValues?: Partial<FormData>;
};

const DEFAULT_VALUES: Partial<FormData> = {
  fullName: '',
  addressLine1: '',
  city: '',
  stateProvince: '',
  zipPostalCode: '',
  country: 'US',
};

export const TributeCardDeliveryDetailsForm: FC<Props> = (props) => {
  const { onSubmitRequest, slots, defaultValues = DEFAULT_VALUES, onFieldChange } = props;
  const dictionary = useDictionary();

  const methods = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { handleSubmit, formState } = methods;

  const onSubmit = useCallback(async (data: FormData) => {
    const payload = {
      ...data,
    };

    return onSubmitRequest(payload);
  }, [onSubmitRequest]);

  return (
    <FormProvider {...methods}>
      <form style={{ width: '100%', height: '100%' }} onSubmit={handleSubmit(onSubmit)}>
        <Stack justifyContent="space-between" height="100%" gap={4}>
          <Grid container spacing={2}>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="fullName"
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="secondary"
                        placeholder="Enter the recipient's full name"
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          onFieldChange?.('fullName', e.target.value);
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              'fieldset': {
                                borderColor: '#e2e8f0',
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    )}
                  />
                }
                label="Full Name"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="addressLine1"
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="secondary"
                        placeholder="Enter the recipient's address"
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          onFieldChange?.('addressLine1', e.target.value);
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              'fieldset': {
                                borderColor: '#e2e8f0',
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    )}
                  />
                }
                label="Address"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="city"
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="secondary"
                        placeholder="City"
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          onFieldChange?.('city', e.target.value);
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              'fieldset': {
                                borderColor: '#e2e8f0',
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    )}
                  />
                }
                label="City"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="stateProvince"
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="secondary"
                        placeholder="State"
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          onFieldChange?.('stateProvince', e.target.value);
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              'fieldset': {
                                borderColor: '#e2e8f0',
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    )}
                  />
                }
                label="State"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="zipPostalCode"
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="secondary"
                        placeholder="Zip Code"
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          onFieldChange?.('zipPostalCode', e.target.value);
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              'fieldset': {
                                borderColor: '#e2e8f0',
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    )}
                  />
                }
                label="Zip Code"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="country"
                    rules={{
                      required: dictionary.forms.validations.required,
                    }}
                    render={({ field: { ref, onChange, ...field }, fieldState }) => (
                      <CountrySelect
                        defaultValue="US"
                        inputRef={ref}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          onChange(e);
                          onFieldChange?.('country', e.target.value);
                        }}
                        {...field}
                      />
                    )}
                  />
                }
                label="Country"
              />
            </Grid>
          </Grid>

          {typeof slots?.footer === 'function' ? slots?.footer(formState) : slots?.footer}
        </Stack>
      </form>
    </FormProvider>
  );
};
